import { skipToken } from '@reduxjs/toolkit/query';
import { formatDate } from '@strata/helper-functions';
import { FormSection, FormSectionWithRows } from '@strata/react-form-section';
import { AddressDetailLabelValue, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { ClaimTimeline } from '@strata/react-timeline';
import { useParams } from 'react-router';

import { ClaimFrequencyTypeIcon } from 'components/shared/ClaimFrequencyTypeIcon';
import { ClaimPlaceOfServiceMap } from 'services/MedicalBillingConstants';
import { type ClaimDetails, useGetClaimMilestonesQuery } from 'store/api/medbillingApi';
import { useGetSupplierDetailsQuery } from 'store/api/referenceApi';

interface OverviewTabProps {
    claimDetails?: ClaimDetails;
    claimDetailsLoading?: boolean;
}

export const OverviewTab = ({
    claimDetails = undefined,
    claimDetailsLoading = true,
}: OverviewTabProps) => {
    const { payerId, transactionId } = useParams() as { payerId: string; transactionId: string };
    const { data: claimMilestones, isFetching: claimMilestonesLoading } =
        useGetClaimMilestonesQuery({ payerId, transactionId });

    const { data: supplierDetails, isFetching: supplierDetailsLoading } =
        useGetSupplierDetailsQuery(
            claimDetails?.supplierNpi ? { supplierNpi: claimDetails.supplierNpi } : skipToken,
        );

    return (
        <div className="sds_claim-details-overview">
            <div className="sds_claim-details-overview__container" style={{ flex: '2 0' }}>
                <FormSectionWithRows
                    sectionTitle="Claim Details"
                    isLoading={claimDetailsLoading || supplierDetailsLoading}
                    rowOneContent={
                        <>
                            <DetailLabelValuePair
                                style={{ width: '33%' }}
                                label="Pharmacy"
                                value={supplierDetails?.organizationName}
                            />
                            <DetailLabelValuePair
                                style={{ width: '33%' }}
                                label="Pharmacy NPI"
                                value={supplierDetails?.supplierNpi}
                            />
                            <AddressDetailLabelValue
                                style={{ width: '34%' }}
                                label="Pharmacy Billing Address"
                                address1={supplierDetails?.address1}
                                address2={supplierDetails?.address2}
                                city={supplierDetails?.city}
                                state={supplierDetails?.state}
                                zip={supplierDetails?.zip}
                            />
                        </>
                    }
                    rowTwoContent={
                        <>
                            <DetailLabelValuePair
                                style={{ width: '33%' }}
                                label="Place Of Service (From Claim)"
                                value={
                                    claimDetails?.placeOfService &&
                                    ClaimPlaceOfServiceMap[claimDetails.placeOfService]
                                }
                            />
                            <DetailLabelValuePair
                                style={{ width: '33%' }}
                                label="Payer Claim ID"
                                value={claimDetails?.payerClaimNumber}
                            />
                            <DetailLabelValuePair
                                label="Claim Frequency"
                                value={
                                    <ClaimFrequencyTypeIcon
                                        claimFrequencyType={claimDetails?.claimFrequencyType}
                                    />
                                }
                            />
                        </>
                    }
                />
                <FormSectionWithRows
                    sectionTitle="Member Details"
                    isLoading={claimDetailsLoading}
                    rowOneContent={
                        <>
                            <DetailLabelValuePair
                                style={{ width: '33%' }}
                                label="First Name"
                                value={claimDetails?.memberFirstName}
                            />
                            <DetailLabelValuePair
                                style={{ width: '33%' }}
                                label="Member ID"
                                value={claimDetails?.memberId}
                            />
                            <DetailLabelValuePair
                                style={{ width: '34%' }}
                                label="Member DOB"
                                value={formatDate(claimDetails?.memberDOB)}
                            />
                        </>
                    }
                />
            </div>
            <div className="sds_claim-details-overview__container" style={{ flex: '1 0' }}>
                <FormSection
                    sectionTitle="Claim Timeline"
                    isLoading={claimMilestonesLoading}
                    content={<ClaimTimeline claimMilestones={claimMilestones} />}
                />
            </div>
        </div>
    );
};
